/**
 * VaultSlugService - Single source of truth for all slug operations
 *
 * This service provides consistent slug handling across the entire vault system.
 * All code that needs to work with document slugs should use this service.
 *
 * Key principles:
 * 1. URL slugs are always lowercase kebab-case for consistency
 * 2. Storage slugs (database) match URL slugs exactly
 * 3. Slug→title reconstruction is lossy; prefer currentPath/filesystem for exact display
 * 4. All comparisons are case-insensitive
 */

export interface SlugOptions {
  /**
   * Azure Wiki mode: decode %2D (hyphens) and %20 (spaces) before slugifying.
   */
  azureMode?: boolean;
}

/**
 * Convert a title/name to a URL-safe slug.
 * This is THE canonical function for generating slugs used in URLs and database storage.
 *
 * - Lowercase
 * - Spaces → hyphens
 * - Collapse consecutive hyphens
 * - Trim leading/trailing hyphens
 *
 * @example
 * titleToUrlSlug("My Document Title") → "my-document-title"
 * titleToUrlSlug("  Untitled  ") → "untitled"
 * titleToUrlSlug("Some - Thing") → "some-thing"
 */
export function titleToUrlSlug(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return '';

  return trimmed
    .replace(/[|｜ㅣ]/g, ' ') // treat divider variants as separators
    .toLowerCase()
    .replace(/\s+-\s+/g, '-') // " - " → single hyphen (common separator)
    .replace(/\s+/g, '-') // spaces → hyphens
    .replace(/-+/g, '-') // collapse consecutive hyphens
    .replace(/^-|-$/g, ''); // trim leading/trailing hyphens
}

/**
 * Convert a title to a filesystem-safe name PRESERVING casing and spaces.
 * Use this when writing to disk - the filename will keep the user's original
 * casing and spaces. For URLs/DB slugs, use titleToUrlSlug() instead.
 *
 * This function:
 * - Preserves original casing
 * - Preserves spaces (unlike titleToUrlSlug which converts to hyphens)
 * - Removes invalid filesystem characters (<>:"/\|?*)
 * - Collapses multiple consecutive spaces
 * - Trims leading/trailing whitespace
 *
 * @example
 * titleToFilesystemName("My Document Title") → "My Document Title"
 * titleToFilesystemName("8. Policies - old") → "8. Policies - old"
 * titleToFilesystemName("File<Name>Test") → "FileName Test"
 */
export function titleToFilesystemName(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return '';

  return trimmed
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filesystem chars
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
}

/**
 * Convert a file/folder name to a URL slug.
 * Strips file extensions (.md, .mdx) before slugifying.
 *
 * @example
 * fileNameToUrlSlug("My Document.mdx") → "my-document"
 * fileNameToUrlSlug("folder-name") → "folder-name"
 */
export function fileNameToUrlSlug(fileName: string, options: SlugOptions = {}): string {
  // Strip common extensions
  let base = fileName.replace(/\.(mdx?|md|tsx?|jsx?)$/i, '').trim();

  // Azure Wiki mode: decode encoded characters first
  if (options.azureMode) {
    base = decodeAzureWikiName(base);
  }

  return titleToUrlSlug(base);
}

/**
 * Convert a relative file path to a URL slug path.
 * Each segment is individually slugified.
 *
 * @example
 * filePathToUrlSlugPath("folder/My Document.mdx") → "folder/my-document"
 * filePathToUrlSlugPath("Parent Folder/Child Folder/File.md") → "parent-folder/child-folder/file"
 */
export function filePathToUrlSlugPath(relativePath: string, options: SlugOptions = {}): string {
  const normalized = relativePath.replace(/\\/g, '/');
  const segments = normalized.split('/').filter(Boolean);

  return segments
    .map((segment) => fileNameToUrlSlug(segment, options))
    .filter((segment) => segment.length > 0)
    .join('/');
}

/**
 * Convert a URL slug back to a display-friendly title.
 * Capitalizes first letter of each word.
 *
 * @example
 * slugToDisplayTitle("my-document-title") → "My Document Title"
 */
export function slugToDisplayTitle(slug: string): string {
  if (!slug) return '';

  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Check if two slugs are equivalent (case-insensitive comparison).
 * Useful for deduplication and collision detection.
 *
 * @example
 * slugsAreEquivalent("My-Doc", "my-doc") → true
 * slugsAreEquivalent("doc-1", "doc-2") → false
 */
export function slugsAreEquivalent(slug1: string, slug2: string): boolean {
  return titleToUrlSlug(slug1) === titleToUrlSlug(slug2);
}

/**
 * Build a full slug path from folder and filename.
 *
 * @example
 * buildSlugPath("parent/folder", "my-document") → "parent/folder/my-document"
 * buildSlugPath("", "my-document") → "my-document"
 */
export function buildSlugPath(folderPath: string, slug: string): string {
  const folder = folderPath.trim().replace(/^\/|\/$/g, '');
  const file = slug.trim().replace(/^\/|\/$/g, '');

  if (!folder) return file;
  if (!file) return folder;
  return `${folder}/${file}`;
}

/**
 * Decode Azure Wiki encoded name.
 * Azure Wiki uses %2D for hyphens and %20 for spaces.
 */
function decodeAzureWikiName(name: string): string {
  const withSpaces = name.replace(/-\.-/g, '-');
  try {
    return decodeURIComponent(withSpaces)
      .replace(/-/g, ' '); // remaining hyphens become spaces
  } catch {
    return withSpaces
      .replace(/%2D/gi, '-')
      .replace(/%20/gi, ' ')
      .replace(/%26/gi, '&')
      .replace(/-/g, ' '); // remaining hyphens become spaces
  }
}

/**
 * For backward compatibility with VaultSlugCodec.
 * Use titleToUrlSlug or fileNameToUrlSlug instead.
 *
 * @deprecated Use titleToUrlSlug() or fileNameToUrlSlug() instead
 */
export function toCanonicalSlugSegmentFromName(name: string, options: SlugOptions = {}): string {
  return fileNameToUrlSlug(name, options);
}

/**
 * For backward compatibility with VaultSlugCodec.
 * Use filePathToUrlSlugPath instead.
 *
 * @deprecated Use filePathToUrlSlugPath() instead
 */
export function toCanonicalSlugPath(relativePath: string, options: SlugOptions = {}): string {
  return filePathToUrlSlugPath(relativePath, options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Maximum length for canonical slugs */
export const MAX_SLUG_LENGTH = 48;

/** Regex for canonical slug format: lowercase alphanumeric with hyphens, no leading/trailing hyphens */
export const CANONICAL_SLUG_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/**
 * Validates a vault/org title for forbidden patterns.
 * Returns an error message if invalid, null if valid.
 *
 * Rejects:
 * - Path traversal patterns (.., /, \)
 * - Null bytes
 * - Titles that would slugify to empty string
 */
export function validateVaultTitle(title: string): string | null {
  const trimmed = title.trim();

  // Empty is typically handled by required field validation
  if (!trimmed) return null;

  // Check for path traversal patterns
  if (trimmed.includes('..')) {
    return 'Name cannot contain path traversal patterns (..)';
  }
  if (trimmed.includes('/') || trimmed.includes('\\')) {
    return 'Name cannot contain slashes';
  }

  // Check for null bytes
  if (trimmed.includes('\0')) {
    return 'Name contains invalid characters';
  }

  // Check if the slugified result would be empty (only special chars)
  const slug = titleToUrlSlug(trimmed);
  if (!slug) {
    return 'Name must contain at least one letter or number';
  }

  return null;
}

/**
 * Validates that a slug is in canonical format.
 * Returns an error message if invalid, null if valid.
 *
 * Canonical format:
 * - Lowercase letters (a-z), numbers (0-9), and hyphens (-) only
 * - Must start and end with alphanumeric character
 * - No leading/trailing hyphens
 * - Maximum 48 characters
 * - No path traversal characters (.., /, \, null bytes)
 */
export function validateSlug(slug: string): string | null {
  if (!slug) {
    return 'Slug cannot be empty';
  }

  // Check for null bytes
  if (slug.includes('\0')) {
    return 'Slug contains invalid characters';
  }

  // Check for path traversal attempts
  if (slug.includes('..') || slug.includes('/') || slug.includes('\\')) {
    return 'Slug contains invalid characters';
  }

  // Check length
  if (slug.length > MAX_SLUG_LENGTH) {
    return `Slug must be ${MAX_SLUG_LENGTH} characters or less`;
  }

  // Check canonical format (lowercase alphanumeric + hyphens, proper start/end)
  if (!CANONICAL_SLUG_REGEX.test(slug)) {
    return 'Slug must contain only lowercase letters, numbers, and hyphens, and must start and end with a letter or number';
  }

  return null;
}
